import { toNestErrors } from "@hookform/resolvers";
import { stripEmptyDeep } from "@inrev/common";
import { Option } from "@inrev/core";
import {
	FieldError,
	FieldValues,
	Path,
	Resolver,
	ResolverOptions,
	UseFormReturn,
} from "react-hook-form";
import { z } from "zod";
import {
	isFieldPartial as checkFieldPartial,
	isFieldRequiredForApproval as checkFieldRequiredForApproval,
	isEmpty,
} from "./schema-fields";

export const stripEmptyResolver = <
	TFieldValues extends FieldValues,
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	TContext extends any = any,
	TTransformedValues = TFieldValues,
	TOptions extends ResolverOptions<TFieldValues> = ResolverOptions<TFieldValues>,
>(
	resolver: Resolver<TFieldValues, TContext, TTransformedValues>,
	returnEmptyObject: boolean = true,
	returnEmptyArray: boolean = false,
): Resolver<TFieldValues, TContext, TTransformedValues> => {
	return (async (values: TFieldValues, context: TContext, options: TOptions) => {
		return await resolver(
			stripEmptyDeep(values, returnEmptyObject, returnEmptyArray),
			context,
			options,
		);
	}) as Resolver<TFieldValues, TContext, TTransformedValues>;
};

/**
 * RHF resolver that validates with the app's Zod instance.
 *
 * `@hookform/resolvers/zod` vendors its own Zod 4 copy for parsing, so it ignores
 * the portal `z.config({ customError })` and empty required fields become
 * "Invalid input" instead of "Required". Parsing via the schema's own
 * `safeParseAsync` keeps the global error map in effect for every form that
 * uses this helper.
 */
export const typedZodResolver = <Input extends FieldValues, Context, Output>(
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	schema: z.ZodType<Output, Input, any>,
	_schemaOptions?: z.core.ParseContext<z.core.$ZodIssue>,
	resolverOptions?: {
		mode?: "async" | "sync";
		raw?: boolean;
	},
): Resolver<Input, Context, Output> => {
	return async (values, _context, options) => {
		const result =
			resolverOptions?.mode === "sync"
				? schema.safeParse(values)
				: await schema.safeParseAsync(values);

		if (result.success) {
			return {
				values: (resolverOptions?.raw ? values : result.data) as Output,
				errors: {},
			};
		}

		const fieldErrors: Record<string, FieldError> = {};
		for (const issue of result.error.issues) {
			if (issue.path.length === 0) continue;
			const path = issue.path.join(".");
			if (fieldErrors[path]) continue;
			fieldErrors[path] = { message: issue.message, type: issue.code };
		}

		return {
			values: {} as Output,
			errors: toNestErrors(fieldErrors, options),
		};
	};
};

export { getNestedValue, hasOwnPath, isEmpty } from "./schema-fields";

/**
 * One field of a PATCH body: the submitted value when it differs from what is stored, and
 * `undefined` — omit the field — when it does not. Treats absent and null as the same input so a
 * field the resolver stripped reads as a clear rather than a change, but returns null explicitly
 * so the server distinguishes "clear this" from "leave it alone".
 */
export const changedValue = <T>(next: Option<T>, current: Option<T>): T | null | undefined => {
	const value = Option.toNull(next);
	return value === Option.toNull(current) ? undefined : value;
};

/**
 * Checks if a field should be considered valid when the form has been submitted.
 * Returns true if:
 * - The form has been submitted AND
 * - The field is not required (no validation error when empty) AND
 * - The field value is empty/blank
 *
 * This function determines if a field is required by checking if the resolver
 * throws a validation error when the field is empty.
 *
 * @param formMethods - The react-hook-form methods object
 * @param fieldName - The field name to check (supports dot notation for nested fields)
 * @returns boolean indicating if the field should be considered valid
 */
export const isPartialField = <T extends FieldValues>(
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	formMethods: UseFormReturn<T, any, any>,
	fieldName: Path<T>,
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	schema?: z.ZodObject<any>,
	requiresSubmit = true,
): boolean => {
	const { formState } = formMethods;
	if (requiresSubmit && !formState.isSubmitted) return false;
	if (!schema) return false;
	return checkFieldPartial(schema, fieldName) && isEmpty(formMethods.getValues(fieldName));
};

export const isRequiredForApprovalField = <T extends FieldValues>(
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	formMethods: UseFormReturn<T, any, any>,
	fieldName: Path<T>,
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	schema?: z.ZodObject<any>,
	requiresSubmit = true,
): boolean => {
	const { formState } = formMethods;
	if (requiresSubmit && !formState.isSubmitted) return false;
	if (!schema) return false;
	return (
		checkFieldRequiredForApproval(schema, fieldName) && isEmpty(formMethods.getValues(fieldName))
	);
};

type OptionalOrNullable = z.ZodOptional<z.ZodType> | z.ZodNullable<z.ZodType>;
const isOptionalOrNullable = (s: z.ZodType): s is OptionalOrNullable =>
	s instanceof z.ZodOptional || s instanceof z.ZodNullable;

// biome-ignore lint/suspicious/noExplicitAny: legacy any
const isPlainObject = (v: unknown): v is Record<string, any> =>
	v !== null && typeof v === "object" && !Array.isArray(v);

const mergeDefaults = (
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	target: Record<string, any>,
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	source: Record<string, any>,
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
): Record<string, any> => {
	const result = { ...target };
	for (const key of Object.keys(source)) {
		const t = result[key];
		const s = source[key];
		if (s === null && (t === undefined || t === null)) {
			result[key] = null;
		} else if (s === null) {
			// source says null but target has a value — keep target
		} else if (t === null && s !== undefined) {
			result[key] = s;
		} else if (t === null) {
			result[key] = null;
		} else if (Array.isArray(s) || Array.isArray(t)) {
			result[key] = [];
		} else if (isPlainObject(t) && isPlainObject(s)) {
			result[key] = mergeDefaults(t, s);
		} else if (t === undefined) {
			result[key] = s;
		}
	}
	return result;
};

// biome-ignore lint/suspicious/noExplicitAny: legacy any
const getVariantDefaults = (variants: readonly unknown[]): Record<string, any> | undefined => {
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	let merged: Record<string, any> | undefined;

	for (const variant of variants) {
		if (!(variant instanceof z.ZodType)) continue;
		const defaults = getSchemaDefaults(variant);
		if (isPlainObject(defaults)) {
			merged = merged ? mergeDefaults(merged, defaults) : defaults;
		}
	}

	return merged;
};
/** For unions and discriminated unions, merges the default values of all variants. */
// biome-ignore lint/suspicious/noExplicitAny: legacy any
export const getSchemaDefaults = (schema: z.ZodTypeAny): any => {
	if (isOptionalOrNullable(schema)) {
		const inner = getSchemaDefaults(schema.unwrap());
		if (Array.isArray(inner)) return inner;
		return schema instanceof z.ZodNullable ? null : undefined;
	}
	if (schema instanceof z.ZodDefault) return undefined;
	if (schema instanceof z.ZodPipe && schema.out instanceof z.ZodType)
		return getSchemaDefaults(schema.out);

	if (schema instanceof z.ZodObject) {
		// biome-ignore lint/suspicious/noExplicitAny: legacy any
		const defaults: Record<string, any> = {};
		for (const [key, fieldSchema] of Object.entries(schema.shape)) {
			defaults[key] = getSchemaDefaults(fieldSchema);
		}
		return defaults;
	}

	if (schema instanceof z.ZodArray || schema instanceof z.ZodTuple) {
		return [];
	}

	if (schema instanceof z.ZodDiscriminatedUnion || schema instanceof z.ZodUnion) {
		const merged = getVariantDefaults(schema.options);
		if (merged) return merged;
	}

	const meta = typeof schema.meta === "function" ? schema.meta() : undefined;
	if (Array.isArray(meta?.__variants)) {
		const merged = getVariantDefaults(meta.__variants);
		if (merged) return merged;
	}

	return undefined;
};
